// @file Vue composable for PostHog
import environment from '@@/bits/environment'
import { isAppUsing } from '@@/bits/flip'
import type { CaptureResult, PostHog, PostHogConfig } from 'posthog-js'

const POSTHOG_TOKEN =
  environment === 'production'
    ? 'phc_Qq7IdaGdpavwycUT45k9GGcwMUjV1XIRx3zGRe1Nxrc'
    : 'phc_EPCt6qTBrd32zI0egjOAA8SoeriXYmG5y369SNYZNOh'

// Decided once per module load (i.e. once per page load), not re-rolled on every usePostHog() call —
// usePostHog() can be invoked multiple times per page (identify, onSubmit, onDismiss, etc.), and
// re-rolling each time would inflate the effective sampling rate well above 0.1%.
const IS_IN_SESSION_RECORDING_SAMPLE = Math.random() < 0.001

const NOOP_POSTHOG = new Proxy(
  {},
  {
    get: () => () => undefined,
  },
) as PostHog

// Module-level cache: every caller shares this exact same promise, so posthog.init() runs exactly once
// per page load instead of once per caller. Calling posthog.init() more than once reconfigures the same
// singleton and can reset in-flight survey/feature-flag state.
let posthogPromise: ReturnType<typeof initPostHog> | undefined
function usePostHog(): ReturnType<typeof initPostHog> {
  posthogPromise ??= initPostHog().catch((e) => {
    // Clear the cache on failure so a later call can retry -- otherwise a single transient failure
    // (e.g. a chunk-load blip on the dynamic import) would permanently cache a rejected promise, making
    // PostHog unavailable for the rest of the page load even though the underlying cause was transient.
    posthogPromise = undefined
    throw e
  })
  return posthogPromise
}

let isScrollmapCaptureEnabled = false
function enableScrollmapCapture(): void {
  isScrollmapCaptureEnabled = true
}

// The events PostHog captures for itself. Everything a landside surface captures deliberately
// goes through bits/landside_analytics, which already stamps these properties on it.
const AUTOMATIC_PAGEVIEW_EVENTS = new Set(['$pageview', '$pageleave'])

// Supplied by the landside entrypoint; undefined on every other surface, so the hook below is
// inert for the surface and dashboard apps that share this client.
let readLandsidePageviewProperties: (() => Record<string, unknown>) | undefined

/**
 * Give the client a live reader for the funnel properties its own pageviews should carry.
 *
 * Landside CTA and section events are captured through bits/landside_analytics, which stamps
 * `page` and `category` on every one. The pageviews PostHog captures for itself never pass
 * through that helper, so without this a `$pageview` filtered on `page` — the denominator of
 * every landside funnel — matches nothing.
 *
 * A reader rather than a fixed object because `page` changes with every SPA navigation, and
 * registered rather than baked in because only landside pages have a page and category to
 * report. The reader answers an empty object for a page whose pageviews should stay
 * unlabeled — which pages report is the store's decision, not this hook's. Register before
 * the first capture: the client stamps its initial pageview during init, which the first
 * warm call triggers.
 */
function setLandsidePageviewProperties(readProperties: () => Record<string, unknown>): void {
  readLandsidePageviewProperties = readProperties
}

// Stamped per event rather than registered as PostHog super properties. Super properties are
// persisted per browser, and this client is shared with the surface and dashboard apps on the
// same origin — every app event from a browser that once visited a landing page would then
// carry that page's `page`/`category`.
function stampLandsidePageviewProperties(event: CaptureResult | null): CaptureResult | null {
  if (event == null || readLandsidePageviewProperties == null) return event
  if (!AUTOMATIC_PAGEVIEW_EVENTS.has(event.event)) return event
  return { ...event, properties: { ...event.properties, ...readLandsidePageviewProperties() } }
}

/** A `before_send` hook: PostHog hands it each event and takes back what it returns. */
type BeforeSendHook = (event: CaptureResult | null) => CaptureResult | null

// We use a config builder so we can apply dynamic flags based on pages determined at runtime.
// `sampleWebVitals` is injected rather than built here because it comes from
// `posthog-js/lib/src/customizations`, a deep import into the SDK's own source: hoisting it to
// module scope would put PostHog back on the boot path of every page importing this composable.
function buildPostHogConfig(sampleWebVitals: BeforeSendHook): Partial<PostHogConfig> {
  return {
    api_host: 'https://echo.padlet.com',
    ui_host: 'https://us.posthog.com',

    // Disable surveys if the flip is not enabled
    disable_surveys: !isAppUsing('posthogNpsSurvey'),

    // Disable automatic analytics events
    autocapture: false,
    // Tracking scrollmap only requires capturing pageview and pageleave events.
    // Setting capture_heatmaps: true is only necessary if we want to track clickmaps specifically.
    // We use `history_change` instead of `true` for landside pages
    // because landside nav uses history.pushState for navigation (see `landsidePages` in pinia/landside_navigation.ts)
    capture_pageview: isScrollmapCaptureEnabled ? 'history_change' : false,
    capture_pageleave: isScrollmapCaptureEnabled,
    capture_dead_clicks: false,

    persistence: 'localStorage',
    person_profiles: 'identified_only',
    // Disable session recording by default so we can control sampling rate programmatically
    disable_session_recording: true,
    before_send: [
      // Stamp the landside funnel properties onto the pageviews PostHog captures itself.
      stampLandsidePageviewProperties,
      // Sample 0.1% of web vitals events if flip is enabled
      sampleWebVitals,
    ],

    property_denylist: ['$device_id', '$window_id'],
  }
}

async function initPostHog() {
  if (environment === 'e2e_test' || environment === 'e2e_ci') return { posthog: NOOP_POSTHOG }

  // `sampleByEvent` is a deep import into posthog-js' own source, so hoisting it to module scope
  // would put the SDK on the boot path of every page importing this composable and make the
  // `import('posthog-js')` below moot.
  const [{ posthog }, { sampleByEvent }] = await Promise.all([
    import('posthog-js'),
    import('posthog-js/lib/src/customizations'),
  ])
  // rebuild the config each time we initso we can apply dynamic flags based on pages determined at runtime (i.e. landside pages)
  posthog.init(
    POSTHOG_TOKEN,
    buildPostHogConfig(sampleByEvent(['$web_vitals'], isAppUsing('posthogSessionRecording') ? 0.1 : 0)),
  )

  if (isAppUsing('posthogSessionRecording') && IS_IN_SESSION_RECORDING_SAMPLE) {
    posthog.startSessionRecording()
  }

  return { posthog }
}

export { enableScrollmapCapture, setLandsidePageviewProperties, usePostHog }
